import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { 
  getMembers, createMember, deleteMember,
  getClasses, createClass, deleteClass,
  checkInMember, getAttendance, getAttendanceStats,
  trackFeatureUsage
} from '../services/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { getErrorMessage } from '../utils/errorUtils';
import { 
  UserCheck,
  Plus,
  Trash2,
  Users,
  Calendar,
  CreditCard,
  Search,
  Clock,
  CheckCircle,
  Scan,
  Zap,
  AlertCircle,
  QrCode
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { cn } from '../lib/utils';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const AttendancePage = () => {
  const { isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Scan Card Mode
  const [scanMode, setScanMode] = useState(false);
  const [scanInput, setScanInput] = useState('');
  const [scanStatus, setScanStatus] = useState(null); // 'success', 'error', 'scanning'
  const [lastCheckedIn, setLastCheckedIn] = useState(null);
  const [selectedClassForScan, setSelectedClassForScan] = useState('');
  const scanInputRef = useRef(null);
  
  // Dialogs
  const [memberDialogOpen, setMemberDialogOpen] = useState(false);
  const [classDialogOpen, setClassDialogOpen] = useState(false);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  
  // Forms
  const [memberForm, setMemberForm] = useState({ name: '', email: '', phone: '' });
  const [classForm, setClassForm] = useState({ name: '', schedule_day: '', schedule_time: '', instructor: '' });
  const [checkInForm, setCheckInForm] = useState({ scan_code: '', class_id: '' });
  
  const [saving, setSaving] = useState(false);
  const [searchCode, setSearchCode] = useState('');

  useEffect(() => {
    loadData();
    trackFeatureUsage('attendance', 'view').catch(() => {});
  }, []);

  const loadData = async () => {
    try {
      const [membersRes, classesRes, attendanceRes, statsRes] = await Promise.all([
        getMembers(),
        getClasses(),
        getAttendance({ date: format(new Date(), 'yyyy-MM-dd') }),
        getAttendanceStats()
      ]);
      setMembers(membersRes.data);
      setClasses(classesRes.data);
      setAttendance(attendanceRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  // Scan Card Handler - Auto check-in when card is scanned
  const handleScanInput = useCallback(async (code) => {
    if (!code || code.length < 3) return;
    
    setScanStatus('scanning');
    try {
      const response = await checkInMember({ 
        scan_code: code.toUpperCase(), 
        class_id: selectedClassForScan && selectedClassForScan !== 'none' ? selectedClassForScan : undefined 
      });
      
      setScanStatus('success');
      setLastCheckedIn(response.data);
      toast.success(`Checked in: ${response.data.member_name}`);
      trackFeatureUsage('attendance', 'scan_checkin').catch(() => {});
      
      // Reload data to show new check-in
      loadData();
      
      // Reset after 3 seconds
      setTimeout(() => {
        setScanStatus(null);
        setLastCheckedIn(null);
      }, 3000);
    } catch (error) {
      setScanStatus('error');
      toast.error(getErrorMessage(error, 'Member not found'));
      
      // Reset after 2 seconds
      setTimeout(() => {
        setScanStatus(null);
      }, 2000);
    }
    
    // Clear input for next scan
    setScanInput('');
  }, [selectedClassForScan]);

  // Handle keyboard input in scan mode
  useEffect(() => {
    if (scanMode && scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [scanMode]);

  // Debounce scan input - wait for complete card scan
  useEffect(() => {
    if (!scanMode || !scanInput) return;
    
    const timer = setTimeout(() => {
      if (scanInput.length >= 6) { // Minimum scan code length
        handleScanInput(scanInput);
      }
    }, 300); // Wait 300ms after last keystroke
    
    return () => clearTimeout(timer);
  }, [scanInput, scanMode, handleScanInput]);

  const handleCreateMember = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createMember(memberForm);
      toast.success('Member added');
      setMemberDialogOpen(false);
      setMemberForm({ name: '', email: '', phone: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add member');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await createClass(classForm);
      toast.success('Class created');
      setClassDialogOpen(false);
      setClassForm({ name: '', schedule_day: '', schedule_time: '', instructor: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to create class');
    } finally {
      setSaving(false);
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await checkInMember({
        scan_code: checkInForm.scan_code || null,
        class_id: checkInForm.class_id || null
      });
      toast.success(`${response.data.member_name} checked in!`);
      setCheckInDialogOpen(false);
      setCheckInForm({ scan_code: '', class_id: '' });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Check-in failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleQuickCheckIn = async (memberId) => {
    try {
      const response = await checkInMember({ member_id: memberId });
      toast.success(`${response.data.member_name} checked in!`);
      loadData();
    } catch (error) {
      toast.error('Check-in failed');
    }
  };

  const handleDeleteMember = async (id) => {
    try {
      await deleteMember(id);
      toast.success('Member removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleDeleteClass = async (id) => {
    try {
      await deleteClass(id);
      toast.success('Class deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete class');
    }
  };

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchCode.toLowerCase()) ||
    m.scan_code.toLowerCase().includes(searchCode.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="attendance-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Member Check-In</p>
            <h1 className="font-heading text-4xl md:text-5xl text-white tracking-tight">
              ATTENDANCE
            </h1>
            <p className="text-zinc-500 mt-2 font-body">Track member check-ins and class attendance</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setScanMode(!scanMode)}
              className={cn(
                "uppercase font-heading tracking-wider transition-all",
                scanMode 
                  ? "bg-green-600 hover:bg-green-700 text-white shadow-[0_0_20px_rgba(34,197,94,0.3)]" 
                  : "bg-ba-surface border border-ba-border hover:border-green-600/50 text-zinc-300"
              )}
              data-testid="scan-mode-btn"
            >
              <Scan className="w-4 h-4 mr-2" />
              {scanMode ? 'Scanning...' : 'Scan Card'}
            </Button>
            <Button 
              onClick={() => setCheckInDialogOpen(true)}
              className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
              data-testid="check-in-btn"
            >
              <UserCheck className="w-4 h-4 mr-2" />
              Manual Check In
            </Button>
          </div>
        </div>

        {/* Scan Card Interface */}
        <AnimatePresence>
          {scanMode && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className={cn(
                "relative border-2 rounded-sm p-8 transition-all",
                scanStatus === 'success' ? "bg-green-600/10 border-green-500" :
                scanStatus === 'error' ? "bg-red-600/10 border-red-500" :
                "bg-ba-surface border-green-600/50"
              )}>
                {/* Scanning pulse animation */}
                {scanStatus === 'scanning' && (
                  <div className="absolute inset-0 rounded-sm overflow-hidden">
                    <div className="absolute inset-0 bg-green-500/10 animate-pulse" />
                  </div>
                )}
                
                <div className="relative flex flex-col lg:flex-row items-center gap-8">
                  {/* Left: Scan Icon */}
                  <div className={cn(
                    "w-24 h-24 rounded-full flex items-center justify-center transition-all",
                    scanStatus === 'success' ? "bg-green-600 shadow-[0_0_30px_rgba(34,197,94,0.5)]" :
                    scanStatus === 'error' ? "bg-red-600 shadow-[0_0_30px_rgba(220,38,38,0.5)]" :
                    "bg-green-600/20 border-2 border-green-600/50"
                  )}>
                    {scanStatus === 'success' ? (
                      <CheckCircle className="w-12 h-12 text-white" />
                    ) : scanStatus === 'error' ? (
                      <AlertCircle className="w-12 h-12 text-white" />
                    ) : (
                      <CreditCard className="w-12 h-12 text-green-500" />
                    )}
                  </div>
                  
                  {/* Middle: Instructions & Input */}
                  <div className="flex-1 text-center lg:text-left">
                    {scanStatus === 'success' && lastCheckedIn ? (
                      <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-green-500 font-body mb-1">Checked In</p>
                        <h2 className="font-heading text-4xl text-white tracking-tight">
                          {lastCheckedIn.member_name.toUpperCase()}
                        </h2>
                        {lastCheckedIn.class_name && (
                          <p className="text-green-400 font-body mt-2">{lastCheckedIn.class_name}</p>
                        )}
                        <p className="text-zinc-500 font-body text-sm mt-1">
                          {format(new Date(), 'h:mm a')}
                        </p>
                      </div>
                    ) : scanStatus === 'error' ? (
                      <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">Not Found</p>
                        <h2 className="font-heading text-3xl text-white tracking-tight">
                          INVALID CARD
                        </h2>
                        <p className="text-zinc-500 font-body mt-2">Please try again or use manual check-in</p>
                      </div>
                    ) : (
                      <div>
                        <p className="text-[10px] tracking-[0.3em] uppercase text-green-500 font-body mb-1">Ready to Scan</p>
                        <h2 className="font-heading text-3xl text-white tracking-tight">
                          SCAN MEMBER CARD
                        </h2>
                        <p className="text-zinc-500 font-body mt-2">
                          Swipe card or type member code below
                        </p>
                        
                        {/* Hidden input for card reader / keyboard */}
                        <div className="mt-4 flex flex-col sm:flex-row gap-3 items-center">
                          <div className="relative w-full sm:w-64">
                            <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                            <Input
                              ref={scanInputRef}
                              value={scanInput}
                              onChange={(e) => setScanInput(e.target.value)}
                              placeholder="Scan or type code..."
                              className="pl-10 h-12 bg-ba-black border-green-600/50 focus:border-green-500 text-lg font-mono tracking-wider"
                              autoFocus
                              data-testid="scan-input"
                            />
                          </div>
                          
                          <Select value={selectedClassForScan} onValueChange={setSelectedClassForScan}>
                            <SelectTrigger className="w-full sm:w-48 h-12 bg-ba-black border-ba-border">
                              <SelectValue placeholder="Select class (optional)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No class</SelectItem>
                              {classes.map(cls => (
                                <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Right: Quick Stats */}
                  <div className="flex gap-6">
                    <div className="text-center">
                      <p className="font-heading text-4xl text-white">{stats?.today_check_ins || 0}</p>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Today</p>
                    </div>
                    <div className="text-center">
                      <p className="font-heading text-4xl text-green-500">{members.length}</p>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body">Members</p>
                    </div>
                  </div>
                </div>
                
                {/* Instructions */}
                <div className="mt-6 pt-4 border-t border-ba-border flex items-center justify-center gap-8 text-sm text-zinc-500">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-green-500" />
                    <span>USB card readers supported</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-green-500" />
                    <span>Member codes: BA-XXXXXXXX</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-ba-surface border border-ba-border p-4 rounded-sm hover:border-red-600/30 transition-all">
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">Today's Check-ins</p>
            <p className="font-heading text-4xl text-white">{stats?.today_check_ins || 0}</p>
          </div>
          <div className="bg-ba-surface border border-ba-border p-4 rounded-sm hover:border-red-600/30 transition-all">
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">This Week</p>
            <p className="font-heading text-4xl text-white">{stats?.week_check_ins || 0}</p>
          </div>
          <div className="bg-ba-surface border border-ba-border p-4 rounded-sm hover:border-red-600/30 transition-all">
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">Total Members</p>
            <p className="font-heading text-4xl text-white">{stats?.total_members || 0}</p>
          </div>
          <div className="bg-ba-surface border border-ba-border p-4 rounded-sm hover:border-red-600/30 transition-all">
            <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">Classes</p>
            <p className="font-heading text-4xl text-white">{classes.length}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="checkins" className="space-y-6">
          <TabsList className="bg-ba-surface border border-ba-border p-1">
            <TabsTrigger value="checkins" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-body">
              <CheckCircle className="w-4 h-4 mr-2" />
              Today's Check-ins
            </TabsTrigger>
            <TabsTrigger value="members" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-body">
              <Users className="w-4 h-4 mr-2" />
              Members
            </TabsTrigger>
            <TabsTrigger value="classes" className="data-[state=active]:bg-red-600 data-[state=active]:text-white font-body">
              <Calendar className="w-4 h-4 mr-2" />
              Classes
            </TabsTrigger>
          </TabsList>

          {/* Today's Check-ins */}
          <TabsContent value="checkins">
            {attendance.length > 0 ? (
              <div className="space-y-3">
                {attendance.map((record, index) => (
                  <motion.div
                    key={record.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-600/20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{record.member_name}</p>
                        {record.class_name && (
                          <p className="text-zinc-500 text-sm">{record.class_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Clock className="w-4 h-4" />
                      {format(parseISO(record.check_in_time), 'h:mm a')}
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-zinc-500">
                <UserCheck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No check-ins today yet</p>
              </div>
            )}
          </TabsContent>

          {/* Members */}
          <TabsContent value="members">
            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                  <Input
                    placeholder="Search by name or scan code..."
                    value={searchCode}
                    onChange={(e) => setSearchCode(e.target.value)}
                    className="pl-10 h-12 bg-zinc-900 border-zinc-800"
                  />
                </div>
                {isAdmin && (
                  <Button onClick={() => setMemberDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                )}
              </div>

              {filteredMembers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredMembers.map((member) => (
                    <motion.div
                      key={member.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-medium">{member.name}</p>
                          {member.email && <p className="text-zinc-500 text-sm">{member.email}</p>}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleQuickCheckIn(member.id)}
                            className="bg-green-600 hover:bg-green-700 h-8"
                          >
                            <UserCheck className="w-4 h-4" />
                          </Button>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteMember(member.id)}
                              className="text-zinc-400 hover:text-red-500 h-8"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-zinc-400 text-sm bg-zinc-950 p-2 rounded-sm">
                        <QrCode className="w-4 h-4" />
                        <code className="font-mono">{member.scan_code}</code>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No members found</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* Classes */}
          <TabsContent value="classes">
            <div className="space-y-4">
              {isAdmin && (
                <div className="flex justify-end">
                  <Button onClick={() => setClassDialogOpen(true)} className="bg-red-600 hover:bg-red-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Class
                  </Button>
                </div>
              )}

              {classes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {classes.map((cls) => (
                    <motion.div
                      key={cls.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="p-4 bg-zinc-900/50 border border-zinc-800 rounded-sm"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-medium">{cls.name}</p>
                          {cls.instructor && <p className="text-zinc-500 text-sm">with {cls.instructor}</p>}
                        </div>
                        {isAdmin && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClass(cls.id)}
                            className="text-zinc-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <Calendar className="w-4 h-4" />
                        {cls.schedule_day} at {cls.schedule_time}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No classes scheduled</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Check-In Dialog */}
        <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">Member Check-In</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCheckIn} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Scan Code</Label>
                <Input
                  value={checkInForm.scan_code}
                  onChange={(e) => setCheckInForm({...checkInForm, scan_code: e.target.value.toUpperCase()})}
                  placeholder="BA-XXXXXXXX"
                  className="h-12 bg-zinc-950 border-zinc-800 font-mono"
                  data-testid="scan-code-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Class (optional)</Label>
                <Select 
                  value={checkInForm.class_id} 
                  onValueChange={(value) => setCheckInForm({...checkInForm, class_id: value})}
                >
                  <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                    <SelectValue placeholder="Select class (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">General Check-in</SelectItem>
                    {classes.map(cls => (
                      <SelectItem key={cls.id} value={cls.id}>{cls.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setCheckInDialogOpen(false)} className="border-zinc-700">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || !checkInForm.scan_code}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {saving ? 'Processing...' : 'Check In'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Member Dialog */}
        <Dialog open={memberDialogOpen} onOpenChange={setMemberDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">Add Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateMember} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Name *</Label>
                <Input
                  value={memberForm.name}
                  onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                  placeholder="John Smith"
                  className="h-12 bg-zinc-950 border-zinc-800"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input
                  type="email"
                  value={memberForm.email}
                  onChange={(e) => setMemberForm({...memberForm, email: e.target.value})}
                  placeholder="john@example.com"
                  className="h-12 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Phone</Label>
                <Input
                  value={memberForm.phone}
                  onChange={(e) => setMemberForm({...memberForm, phone: e.target.value})}
                  placeholder="+1 (555) 123-4567"
                  className="h-12 bg-zinc-950 border-zinc-800"
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setMemberDialogOpen(false)} className="border-zinc-700">
                  Cancel
                </Button>
                <Button type="submit" disabled={saving || !memberForm.name} className="bg-red-600 hover:bg-red-700">
                  {saving ? 'Adding...' : 'Add Member'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Add Class Dialog */}
        <Dialog open={classDialogOpen} onOpenChange={setClassDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-800">
            <DialogHeader>
              <DialogTitle className="text-white font-heading uppercase">Add Class</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClass} className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Class Name *</Label>
                <Input
                  value={classForm.name}
                  onChange={(e) => setClassForm({...classForm, name: e.target.value})}
                  placeholder="MMA Fundamentals"
                  className="h-12 bg-zinc-950 border-zinc-800"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Day *</Label>
                  <Select 
                    value={classForm.schedule_day} 
                    onValueChange={(value) => setClassForm({...classForm, schedule_day: value})}
                  >
                    <SelectTrigger className="h-12 bg-zinc-950 border-zinc-800">
                      <SelectValue placeholder="Select day" />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS_OF_WEEK.map(day => (
                        <SelectItem key={day} value={day}>{day}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Time *</Label>
                  <Input
                    type="time"
                    value={classForm.schedule_time}
                    onChange={(e) => setClassForm({...classForm, schedule_time: e.target.value})}
                    className="h-12 bg-zinc-950 border-zinc-800"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Instructor</Label>
                <Input
                  value={classForm.instructor}
                  onChange={(e) => setClassForm({...classForm, instructor: e.target.value})}
                  placeholder="Coach Mike"
                  className="h-12 bg-zinc-950 border-zinc-800"
                />
              </div>

              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setClassDialogOpen(false)} className="border-zinc-700">
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={saving || !classForm.name || !classForm.schedule_day || !classForm.schedule_time} 
                  className="bg-red-600 hover:bg-red-700"
                >
                  {saving ? 'Creating...' : 'Create Class'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AttendancePage;
